import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Box,
  CircularProgress,
  Divider,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { CalendarMonth, EnergySavingsLeaf } from "@mui/icons-material";
import { useEffect, useMemo, useRef, useState } from "react";

const STATUS_META = {
  assigned: {
    label: "Assigned Visit",
    legendTitle: "Assigned",
    legendDesc: "Visit is scheduled and upcoming.",
    color: "#2e7d32",
    bg: "#eaf7ea",
  },
  rescheduled: {
    label: "Rescheduled Visit",
    legendTitle: "Rescheduled",
    legendDesc: "Visit date has been changed to a new date.",
    color: "#f9a825",
    bg: "#fff8e1",
  },
  cancelled: {
    label: "Cancelled Visit",
    legendTitle: "Cancelled",
    legendDesc: "Visit has been cancelled.",
    color: "#c62828",
    bg: "#fdecea",
  },
  completed: {
    label: "Completed Visit",
    legendTitle: "Completed",
    legendDesc: "Visit has been completed.",
    color: "#1565c0",
    bg: "#e8f1fb",
  },
};

const LEGEND_ORDER = ["assigned", "rescheduled", "cancelled", "completed"];

const getStatusKey = (status) => {
  const value = String(status || "").trim().toLowerCase();
  if (value === "cancelled") return "cancelled";
  if (value === "rescheduled") return "rescheduled";
  if (value === "completed") return "completed";
  return "assigned";
};

const toDateOnly = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (match) return match[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatShortDate = (value) => {
  const dateOnly = toDateOnly(value);
  if (!dateOnly) return "—";
  const date = new Date(`${dateOnly}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
};

const CalendarCard = styled(Box)({
  width: "100%",
  minWidth: 0,
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  overflow: "hidden",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
});

const CalendarHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "14px 18px",
  background: "linear-gradient(180deg, #f3faf5 0%, #ffffff 70%)",
  borderBottom: "1px solid #eef2f0",
});

const NavButton = styled("button")({
  width: 36,
  height: 36,
  borderRadius: 10,
  border: "1px solid #e5e7eb",
  background: "#fff",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.06)",
  cursor: "pointer",
  color: "#1f2937",
  fontSize: 18,
  lineHeight: 1,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  "&:hover": {
    background: "#f9fafb",
  },
});

const CalendarWrapper = styled(Box)(({ theme }) => ({
  padding: "0 12px 12px",
  width: "100%",
  "& .fc": {
    fontFamily: theme.typography.fontFamily,
    width: "100% !important",
  },
  "& .fc-view-harness": {
    width: "100% !important",
  },
  "& .fc-scrollgrid, & .fc-scrollgrid table, & .fc-daygrid-body, & .fc-scrollgrid-sync-table": {
    width: "100% !important",
  },
  "& .fc-col-header, & .fc-daygrid-body table": {
    width: "100% !important",
    tableLayout: "fixed !important",
  },
  "& .fc-scrollgrid-sync-table": {
    tableLayout: "fixed !important",
  },
  "& .fc-daygrid-day, & .fc-col-header-cell": {
    width: "14.2857% !important",
  },
  "& .fc-toolbar": {
    display: "none !important",
  },
  "& .fc-scrollgrid, & .fc-scrollgrid table": {
    border: "none !important",
    width: "100% !important",
  },
  "& .fc-theme-standard td, & .fc-theme-standard th": {
    borderColor: "#e8eaed !important",
  },
  "& .fc-col-header-cell": {
    background: "#f7f8fa",
    padding: "10px 0",
  },
  "& .fc-col-header-cell-cushion": {
    color: "#1f2937",
    fontWeight: 700,
    fontSize: "0.72rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
  },
  "& .fc .fc-daygrid-day.fc-day-today": {
    backgroundColor: "rgba(46, 125, 50, 0.03)",
  },
  "& .fc .fc-daygrid-day-frame": {
    minHeight: 108,
    padding: "4px 5px 6px",
  },
  "& .fc-daygrid-day-top": {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  "& .fc-daygrid-day-number": {
    fontWeight: 600,
    fontSize: "0.8rem",
    padding: "2px 4px",
    color: "#1f2937",
  },
  "& .fc-day-other .fc-daygrid-day-number": {
    color: "#9ca3af",
  },
  "& .fc-h-event": {
    border: "none",
    background: "transparent",
    boxShadow: "none",
  },
  "& .fc .fc-daygrid-event-harness": {
    marginTop: 3,
  },
  "& .fc-daygrid-more-link": {
    fontSize: "0.7rem",
    fontWeight: 700,
    color: "#2e7d32",
    margin: "4px 2px 0",
    "&:hover": {
      color: "#1b5e20",
      background: "transparent",
    },
  },
  "& .fc .fc-popover": {
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 10px 28px rgba(15, 23, 42, 0.12)",
    zIndex: 1200,
  },
  "& .fc .fc-popover-header": {
    backgroundColor: "#f7f8fa",
    fontWeight: 600,
    fontSize: 12,
  },
}));

const EventPill = styled("div")(({ statuskey }) => {
  const meta = STATUS_META[statuskey] || STATUS_META.assigned;
  return {
    display: "flex",
    alignItems: "flex-start",
    gap: 6,
    width: "100%",
    padding: "5px 7px",
    borderRadius: 8,
    backgroundColor: meta.bg,
    overflow: "hidden",
    cursor: "default",
    boxSizing: "border-box",
  };
});

const StatusPanel = styled(Box)({
  width: "100%",
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: "20px",
  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
  height: "fit-content",
  position: "sticky",
  top: 12,
});

const FieldVisitCalendar = ({ visits = [], loading = false }) => {
  const calendarRef = useRef(null);
  const [title, setTitle] = useState("");

  const events = useMemo(
    () =>
      (Array.isArray(visits) ? visits : [])
        .map((visit) => {
          const date = toDateOnly(visit.visit_date);
          if (!date) return null;
          const statusKey = getStatusKey(visit.visit_status);
          const meta = STATUS_META[statusKey];
          const newDateLabel =
            statusKey === "rescheduled"
              ? `(New Date: ${formatShortDate(visit.visit_date)})`
              : statusKey === "cancelled"
                ? "(New Date: —)"
                : "";
          return {
            id: String(visit.id),
            title: meta.label,
            date,
            extendedProps: {
              statusKey,
              displayLabel: meta.label,
              newDateLabel,
            },
          };
        })
        .filter(Boolean),
    [visits]
  );

  const handlePrev = () => calendarRef.current?.getApi?.()?.prev();
  const handleNext = () => calendarRef.current?.getApi?.()?.next();

  useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return undefined;
    const resize = () => api.updateSize();
    resize();
    window.addEventListener("resize", resize);
    const timer = window.setTimeout(resize, 50);
    return () => {
      window.removeEventListener("resize", resize);
      window.clearTimeout(timer);
    };
  }, [loading, visits]);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          md: "minmax(0, 1fr) 280px",
        },
        gap: 2,
        alignItems: "start",
        width: "100%",
      }}
    >
      <CalendarCard>
        <CalendarHeader>
          <NavButton type="button" onClick={handlePrev} aria-label="Previous month">
            ‹
          </NavButton>
          <Stack direction="row" alignItems="center" spacing={0.75}>
            <EnergySavingsLeaf sx={{ color: "#2e7d32", fontSize: 22 }} />
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "#1f2937",
                letterSpacing: "-0.01em",
              }}
            >
              {title || "Calendar"}
            </Typography>
          </Stack>
          <NavButton type="button" onClick={handleNext} aria-label="Next month">
            ›
          </NavButton>
        </CalendarHeader>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={14}>
            <CircularProgress size={28} />
            <Typography variant="body2" sx={{ ml: 1.5 }} color="text.secondary">
              Loading calendar...
            </Typography>
          </Box>
        ) : (
          <CalendarWrapper>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, interactionPlugin]}
              initialView="dayGridMonth"
              height="auto"
              contentHeight={560}
              events={events}
              dayMaxEventRows={2}
              fixedWeekCount={false}
              headerToolbar={false}
              datesSet={(arg) => setTitle(arg.view.title)}
              eventContent={(arg) => {
                const { statusKey, displayLabel, newDateLabel } = arg.event.extendedProps || {};
                const meta = STATUS_META[statusKey] || STATUS_META.assigned;
                return (
                  <EventPill statuskey={statusKey}>
                    <Box
                      sx={{
                        width: 7,
                        height: 7,
                        borderRadius: "50%",
                        bgcolor: meta.color,
                        mt: "3px",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "0.68rem",
                          fontWeight: 700,
                          color: meta.color,
                          lineHeight: 1.25,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {displayLabel}
                      </Typography>
                      {newDateLabel ? (
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#6b7280",
                            lineHeight: 1.25,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            mt: 0.1,
                          }}
                        >
                          {newDateLabel}
                        </Typography>
                      ) : null}
                    </Box>
                  </EventPill>
                );
              }}
            />
          </CalendarWrapper>
        )}
      </CalendarCard>

      <StatusPanel>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: "1.05rem",
            color: "#1e293b",
            mb: 2.25,
          }}
        >
          Visit Status
        </Typography>

        <Stack spacing={2.25}>
          {LEGEND_ORDER.map((key) => {
            const item = STATUS_META[key];
            return (
              <Stack key={key} direction="row" spacing={1.25} alignItems="flex-start">
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    bgcolor: item.color,
                    mt: "5px",
                    flexShrink: 0,
                  }}
                />
                <Box>
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.9rem",
                      color: "#1e293b",
                      lineHeight: 1.3,
                    }}
                  >
                    {item.legendTitle}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "#64748b",
                      lineHeight: 1.4,
                      mt: 0.25,
                    }}
                  >
                    {item.legendDesc}
                  </Typography>
                </Box>
              </Stack>
            );
          })}
        </Stack>

        <Divider sx={{ my: 2.25, borderColor: "#e5e7eb" }} />

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            bgcolor: "#f0f7ff",
            border: "1px solid #d6e8ff",
            display: "flex",
            gap: 1.25,
            alignItems: "flex-start",
          }}
        >
          <CalendarMonth sx={{ fontSize: 18, color: "#1e3a5f", mt: 0.15 }} />
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "#1e3a5f",
              lineHeight: 1.45,
            }}
          >
            If a visit is rescheduled, the new date will be updated in the system and reminder
            will be triggered for the new visit date.
          </Typography>
        </Box>
      </StatusPanel>
    </Box>
  );
};

export default FieldVisitCalendar;
